import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  AlertTriangle, Bell, CheckCircle, Clock, User, Heart,
  Activity, Thermometer, Pill, Shield, RefreshCw, Plus,
  XCircle, ArrowUp, Eye, Settings, Volume2, VolumeX
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClinicalAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [showCreateAlert, setShowCreateAlert] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  
  const [newAlert, setNewAlert] = useState({
    patient_id: '',
    patient_name: '',
    alert_type: 'vitals',
    level: 'warning',
    title: '',
    message: '',
    source: 'manual'
  });

  const userId = 'demo_user';
  const getToken = () => localStorage.getItem('token');

  useEffect(() => {
    loadData();
    // Poll for new alerts every 30 seconds
    const interval = setInterval(loadAlerts, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadAlerts(), loadStats(), loadRules()]);
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  const loadAlerts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase30/alerts`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAlerts(data.alerts || []);
      }
    } catch (error) {
      console.error('Error loading alerts:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase30/alerts/stats?days=7`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadRules = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase30/alerts/rules`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRules(data.rules || []);
      }
    } catch (error) {
      console.error('Error loading rules:', error);
    }
  };

  const createAlert = async () => {
    if (!newAlert.title || !newAlert.patient_id) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/phase30/alerts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({...newAlert, created_by: userId})
      });

      if (response.ok) {
        toast.success('Alert created successfully');
        setShowCreateAlert(false);
        loadAlerts();
        loadStats();
        setNewAlert({
          patient_id: '',
          patient_name: '',
          alert_type: 'vitals',
          level: 'warning',
          title: '',
          message: '',
          source: 'manual'
        });
      }
    } catch (error) {
      toast.error('Failed to create alert');
    }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      const response = await fetch(`${API_URL}/api/phase30/alerts/${alertId}/acknowledge?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        toast.success('Alert acknowledged');
        loadAlerts();
        loadStats();
      }
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const resolveAlert = async (alertId) => {
    try {
      const response = await fetch(`${API_URL}/api/phase30/alerts/${alertId}/resolve?user_id=${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({ resolution_notes: 'Resolved via dashboard' })
      });
      if (response.ok) {
        toast.success('Alert resolved');
        loadAlerts();
        loadStats();
      }
    } catch (error) {
      toast.error('Failed to resolve alert');
    }
  };

  const escalateAlert = async (alertId) => {
    try {
      const response = await fetch(`${API_URL}/api/phase30/alerts/${alertId}/escalate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({ reason: 'Manual escalation' })
      });
      if (response.ok) {
        toast.warning('Alert escalated');
        loadAlerts();
        loadStats();
      }
    } catch (error) {
      toast.error('Failed to escalate alert');
    }
  };

  const getLevelBadge = (level) => {
    const styles = {
      info: 'bg-blue-100 text-blue-800',
      warning: 'bg-yellow-100 text-yellow-800',
      urgent: 'bg-orange-100 text-orange-800',
      critical: 'bg-red-100 text-red-800 animate-pulse'
    };
    return <Badge className={styles[level] || styles.info}>{level.toUpperCase()}</Badge>;
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'vitals': return <Activity className="h-5 w-5 text-red-500" />;
      case 'medication': return <Pill className="h-5 w-5 text-purple-500" />;
      case 'fall_risk': return <AlertTriangle className="h-5 w-5 text-orange-500" />;
      case 'pain': return <Heart className="h-5 w-5 text-red-500" />;
      case 'symptom': return <Thermometer className="h-5 w-5 text-blue-500" />;
      default: return <Bell className="h-5 w-5 text-gray-500" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="alerts-loading">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  const criticalAlerts = alerts.filter(a => a.level === 'critical');
  const urgentAlerts = alerts.filter(a => a.level === 'urgent');
  const otherAlerts = alerts.filter(a => !['critical', 'urgent'].includes(a.level));

  return (
    <div className="space-y-6" data-testid="clinical-alerts">
      {/* Critical Alert Banner */}
      {criticalAlerts.length > 0 && (
        <div className="bg-red-600 text-white p-4 rounded-lg animate-pulse">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6" />
            <div>
              <p className="font-bold">{criticalAlerts.length} CRITICAL ALERT(S) REQUIRE IMMEDIATE ATTENTION</p>
              <p className="text-sm">{criticalAlerts[0]?.title}</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bell className="h-7 w-7 text-teal-600" />
            Clinical Alerts
          </h1>
          <p className="text-gray-500">Real-time patient alerts and notifications</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={showCreateAlert} onOpenChange={setShowCreateAlert}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700" data-testid="create-alert-btn">
                <Plus className="h-4 w-4 mr-2" />
                Create Alert
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Manual Alert</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Patient ID *</Label>
                  <Input
                    value={newAlert.patient_id}
                    onChange={(e) => setNewAlert({...newAlert, patient_id: e.target.value})}
                    placeholder="Patient ID"
                  />
                </div>
                <div>
                  <Label>Patient Name</Label>
                  <Input
                    value={newAlert.patient_name}
                    onChange={(e) => setNewAlert({...newAlert, patient_name: e.target.value})}
                    placeholder="Patient name"
                  />
                </div>
                <div>
                  <Label>Alert Type</Label>
                  <Select value={newAlert.alert_type} onValueChange={(v) => setNewAlert({...newAlert, alert_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vitals">Vitals</SelectItem>
                      <SelectItem value="medication">Medication</SelectItem>
                      <SelectItem value="fall_risk">Fall Risk</SelectItem>
                      <SelectItem value="pain">Pain</SelectItem>
                      <SelectItem value="symptom">Symptom Change</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Severity Level</Label>
                  <Select value={newAlert.level} onValueChange={(v) => setNewAlert({...newAlert, level: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="info">Info</SelectItem>
                      <SelectItem value="warning">Warning</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Alert Title *</Label>
                  <Input
                    value={newAlert.title}
                    onChange={(e) => setNewAlert({...newAlert, title: e.target.value})}
                    placeholder="Brief alert title"
                  />
                </div>
                <div>
                  <Label>Message</Label>
                  <Textarea
                    value={newAlert.message}
                    onChange={(e) => setNewAlert({...newAlert, message: e.target.value})}
                    placeholder="Detailed message..."
                  />
                </div>
                <Button className="w-full" onClick={createAlert}>Create Alert</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className={stats.by_level?.critical > 0 ? 'border-red-500 border-2' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Critical</p>
                  <p className="text-2xl font-bold text-red-600">{stats.by_level?.critical || 0}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active Alerts</p>
                  <p className="text-2xl font-bold">{stats.active_alerts}</p>
                </div>
                <Bell className="h-8 w-8 text-yellow-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Avg Response</p>
                  <p className="text-2xl font-bold">{stats.avg_response_minutes || 0}m</p>
                </div>
                <Clock className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Resolved (7d)</p>
                  <p className="text-2xl font-bold">{stats.by_status?.resolved || 0}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="active">
            Active Alerts
            {alerts.length > 0 && <Badge className="ml-2 bg-red-500">{alerts.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="rules">Alert Rules</TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-4">
          {alerts.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle className="h-12 w-12 text-green-300 mx-auto mb-4" />
                <p className="text-gray-500">No active alerts</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {[...criticalAlerts, ...urgentAlerts, ...otherAlerts].map((alert) => (
                <Card key={alert.id} className={
                  alert.level === 'critical' ? 'border-red-500 border-2' :
                  alert.level === 'urgent' ? 'border-orange-500 border-2' : ''
                }>
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-full ${
                          alert.level === 'critical' ? 'bg-red-100' :
                          alert.level === 'urgent' ? 'bg-orange-100' : 'bg-gray-100'
                        }`}>
                          {getTypeIcon(alert.alert_type)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-medium">{alert.title}</h3>
                            {getLevelBadge(alert.level)}
                            <Badge variant="outline" className="capitalize">{alert.alert_type}</Badge>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {alert.patient_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatDate(alert.created_at)}
                            </span>
                            {alert.escalated && (
                              <Badge className="bg-purple-100 text-purple-800">
                                Escalated L{alert.escalation_level}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => acknowledgeAlert(alert.id)}>
                          <Eye className="h-4 w-4 mr-1" />
                          Ack
                        </Button>
                        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => resolveAlert(alert.id)}>
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Resolve
                        </Button>
                        {alert.level !== 'critical' && (
                          <Button size="sm" variant="outline" className="text-orange-600" onClick={() => escalateAlert(alert.id)}>
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="rules" className="mt-4">
          <div className="space-y-3">
            {rules.map((rule) => (
              <Card key={rule.id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-teal-100 rounded-lg">
                        <Settings className="h-5 w-5 text-teal-600" />
                      </div>
                      <div>
                        <h3 className="font-medium">{rule.name}</h3>
                        <p className="text-sm text-gray-500 capitalize">Type: {rule.type} • Level: {rule.alert_level}</p>
                      </div>
                    </div>
                    <Badge className={rule.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100'}>
                      {rule.status}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ClinicalAlerts;
